import { GraphEvent } from "@/hooks/queries/useGraphApi";
import { graphClient } from "./GraphClient";
import { showErrorToast } from "@/utils/toastUtils";
import i18next from "i18next";
import axios from "axios";

export class GraphService {
  public async getCurrentUser(): Promise<any> {
    try {
      const user = await graphClient.getMe();
      return {
        id: user.id,
        displayName: user.displayName,
        email: user.mail || user.userPrincipalName,
        jobTitle: user.jobTitle,
        officeLocation: user.officeLocation,
        mobilePhone: user.mobilePhone,
        businessPhones: user.businessPhones
      };
    } catch (error: any) {
      console.error("Error getting current user:", error);
      showErrorToast(
        i18next.t(
          "notification.calendar.permission.getUserFailed.title",
          "Failed to get user information"
        ),
        i18next.t(
          "notification.calendar.permission.getUserFailed.message",
          "Failed to get user information. You might not have sufficient permissions."
        )
      );
      throw error;
    }
  }

  public async getCalendarEvents(startDate: Date, endDate: Date): Promise<any[]> {
    try {
      const startDateTime = startDate.toISOString();
      const endDateTime = endDate.toISOString();

      const response = await graphClient.getCalendarView(startDateTime, endDateTime);
      return response.value || [];
    } catch (error: any) {
      console.error("Error getting calendar events:", error);
      showErrorToast(
        i18next.t(
          "notification.calendar.permission.getEventsFailed.title",
          "Failed to get calendar events"
        ),
        i18next.t(
          "notification.calendar.permission.getEventsFailed.message",
          "Cannot load event details. You might not have sufficient permissions."
        )
      );
      throw error;
    }
  }

  public async getCalendarEvent(eventId: string, userId: string): Promise<any> {
    try {
      const client = graphClient.getClient();
      const selectFields =
        "subject,start,end,locations,body,attendees,organizer,isOnlineMeeting,onlineMeeting,responseStatus,hasAttachments,categories,sensitivity,showAs,iCalUId";
      const url = `/users/${encodeURIComponent(userId)}/events/${eventId}`;
      const event = await client.api(url).select(selectFields).get();
      return event;
    } catch (error: any) {
      console.error("Error getting calendar event:", error);
      throw error;
    }
  }

  public async createCalendarEvent(eventData: GraphEvent): Promise<any> {
    try {
      const { adId, ...eventPayload } = eventData;

      let event;
      if (adId) {
        event = await graphClient.createEventForUser(adId, eventPayload);
      } else {
        event = await graphClient.createEvent(eventPayload);
      }

      return event;
    } catch (error: any) {
      console.error("Error creating calendar event:", error);
      showErrorToast(
        i18next.t(
          "notification.calendar.permission.createEventFailed.title",
          "Failed to create event"
        ),
        i18next.t(
          "notification.calendar.permission.createEventFailed.message",
          "Failed to create event. You might not have sufficient permissions."
        )
      );
      throw error;
    }
  }

  public async updateCalendarEvent(
    adId: string,
    eventId: string,
    eventData: Partial<{
      adId: string;
      iCalUid: string;
      subject: string;
      start: { dateTime: string; timeZone: string };
      end: { dateTime: string; timeZone: string };
      locations?: Array<{ displayName: string }>;
      body?: { contentType: "text" | "html"; content: string };
    }>
  ): Promise<any> {
    try {
      const event = await graphClient.updateEvent(adId || "", eventId, eventData);
      return event;
    } catch (error: any) {
      console.error("Error updating calendar event:", error);
      showErrorToast(
        i18next.t(
          "notification.calendar.permission.updateEventFailed.title",
          "Failed to update event"
        ),
        i18next.t(
          "notification.calendar.permission.updateEventFailed.message",
          "Failed to update event. You might not have sufficient permissions."
        )
      );
      throw error;
    }
  }

  public async deleteCalendarEvent(eventId: string, userId?: string): Promise<boolean> {
    try {
      await graphClient.deleteEvent(eventId, userId || "");
      return true;
    } catch (error: any) {
      console.error("Error deleting calendar event:", error);
      showErrorToast(
        i18next.t(
          "notification.calendar.permission.deleteEventFailed.title",
          "Failed to delete event"
        ),
        i18next.t(
          "notification.calendar.permission.deleteEventFailed.message",
          "Failed to delete event. You might not have sufficient permissions."
        )
      );
      return false;
    }
  }

  public async cancelCalendarEvent(
    eventId: string,
    userId: string,
    comment?: string
  ): Promise<void> {
    try {
      await graphClient.cancelEvent(eventId, userId, comment);
    } catch (error: any) {
      console.error("Error canceling calendar event:", error);
      showErrorToast(
        i18next.t(
          "notification.calendar.permission.cancelEventFailed.title",
          "Failed to cancel event"
        ),
        i18next.t(
          "notification.calendar.permission.cancelEventFailed.message",
          "Failed to cancel event. You might not have sufficient permissions."
        )
      );
      throw error;
    }
  }

  public async getUserTimezone(): Promise<string> {
    try {
      // Use Intl API to get browser's timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return timezone || "UTC";
    } catch (error: any) {
      console.error("Error getting user timezone:", error);
      return "UTC";
    }
  }

  public async getUserPhotoUrl(): Promise<string | null> {
    try {
      const photoBlob = await graphClient.getUserPhoto();
      return URL.createObjectURL(photoBlob);
    } catch (error: any) {
      console.error("Error getting user photo:", error);
      return null;
    }
  }

  public async getUserPhotoUrlById(userIdOrEmail: string): Promise<string | null> {
    try {
      const photoBlob = await graphClient.getUserPhotoById(userIdOrEmail);
      if (photoBlob) {
        return URL.createObjectURL(photoBlob);
      }
      return null;
    } catch (error: any) {
      console.error("Error getting user photo:", error);
      return null;
    }
  }

  public async getBatchUserPhotos(userIdsOrEmails: string[]): Promise<Map<string, string>> {
    try {
      return await graphClient.getBatchUserPhotos(userIdsOrEmails);
    } catch (error: any) {
      console.error("Error getting batch user photos:", error);
      return new Map();
    }
  }

  public isReady(): boolean {
    return graphClient.isInitialized();
  }

  public async initializeWithToken(token: string, expiresOn?: Date | string | null): Promise<void> {
    await graphClient.initializeWithToken(token, expiresOn);
  }

  public setAccessToken(token: string, expiresOn?: Date | string | null): void {
    graphClient.setAccessToken(token, expiresOn);
  }

  public clear(): void {
    graphClient.clear();
  }

  public async getUserCategories(adId: string): Promise<any[]> {
    try {
      const response = await graphClient.getUserCategories(adId);
      return response.value || [];
    } catch (error: any) {
      console.error("Error getting user categories:", error);
      return [];
    }
  }

  public async getCalendarPermissions(): Promise<any[]> {
    try {
      const response = await graphClient.getCalendarPermissions();
      return response.value || [];
    } catch (error: any) {
      console.error("Error getting calendar permissions:", error);
      showErrorToast(
        i18next.t(
          "notification.calendar.permission.getFailed.title",
          "Failed to get calendar permissions"
        ),
        i18next.t(
          "notification.calendar.permission.getFailed.message",
          "Failed to get calendar permissions. Please try again."
        )
      );
      throw error;
    }
  }

  public async getAllowedRolesForEmail(email: string): Promise<string[]> {
    try {
      return await graphClient.getAllowedRolesForEmail(email);
    } catch (error: any) {
      console.error("Error getting allowed roles:", error);
      // Return default roles on error
      return ["freeBusyRead", "limitedRead", "read"];
    }
  }

  public async shareCalendar(email: string, role: string): Promise<any> {
    try {
      const permission = {
        emailAddress: {
          address: email
        },
        role: role
      };
      const result = await graphClient.createCalendarPermission(permission);
      return result;
    } catch (error: any) {
      console.error("Error sharing calendar:", error);
      showErrorToast(
        i18next.t("notification.calendar.permission.shareFailed.title", "Failed to share calendar"),
        i18next.t(
          "notification.calendar.permission.shareFailed.message",
          "Failed to share calendar. Please try again."
        )
      );
      throw error;
    }
  }

  public async updateCalendarPermission(permissionId: string, role: string): Promise<any> {
    try {
      const result = await graphClient.updateCalendarPermission(permissionId, role);
      return result;
    } catch (error: any) {
      console.error("Error updating calendar permission:", error);
      showErrorToast(
        i18next.t(
          "notification.calendar.permission.updateFailed.title",
          "Failed to update permission"
        ),
        i18next.t(
          "notification.calendar.permission.updateFailed.message",
          "Failed to update permission. Please try again."
        )
      );
      throw error;
    }
  }

  public async removeCalendarPermission(permissionId: string): Promise<void> {
    try {
      await graphClient.deleteCalendarPermission(permissionId);
    } catch (error: any) {
      console.error("Error removing calendar permission:", error);
      showErrorToast(
        i18next.t(
          "notification.calendar.permission.removeFailed.title",
          "Failed to remove permission"
        ),
        i18next.t(
          "notification.calendar.permission.removeFailed.message",
          "Failed to remove permission. Please try again."
        )
      );
      throw error;
    }
  }

  public async findMeetingTimes(params: {
    attendees: Array<{
      type: "required" | "optional";
      emailAddress: {
        name?: string;
        address: string;
      };
    }>;
    timeConstraint?: {
      activityDomain?: "work" | "personal" | "unrestricted";
      timeSlots?: Array<{
        start: {
          dateTime: string;
          timeZone: string;
        };
        end: {
          dateTime: string;
          timeZone: string;
        };
      }>;
    };
    meetingDuration?: string;
    maxCandidates?: number;
    isOrganizerOptional?: boolean;
    returnSuggestionReasons?: boolean;
    minimumAttendeePercentage?: number;
    locationConstraint?: any;
  }): Promise<any> {
    try {
      const result = await graphClient.findMeetingTimes(params);
      return result;
    } catch (error: any) {
      console.error("Error finding meeting times:", error);
      showErrorToast(
        i18next.t(
          "notification.calendar.permission.findMeetingFailed.title",
          "Failed to find meeting times"
        ),
        i18next.t(
          "notification.calendar.permission.findMeetingFailed.message",
          "Failed to find meeting times. Please try again."
        )
      );
      throw error;
    }
  }

  public async getPeople(options?: {
    search?: string;
    filter?: string;
    orderBy?: string;
    select?: string[];
    top?: number;
    skip?: number;
  }): Promise<any[]> {
    try {
      const response = await graphClient.getPeople(options);
      return response.value || [];
    } catch (error: any) {
      console.error("Error getting people:", error);
      showErrorToast(
        i18next.t("notification.calendar.permission.getPeopleFailed.title", "Failed to get people"),
        i18next.t(
          "notification.calendar.permission.getPeopleFailed.message",
          "Failed to get people. Please try again."
        )
      );
      throw error;
    }
  }

  public async getOAuthToken(
    assertionToken: string,
    scope: string
  ): Promise<{
    accessToken: string;
    expiresIn: number | null;
    refreshToken: string | null;
  }> {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_BASE_URL_API}/group-calendars/graph-token`,
        {
          scope: scope
        },
        {
          headers: {
            Authorization: `Bearer ${assertionToken}`,
            "Content-Type": "application/json"
          }
        }
      );
      return response.data;
    } catch (error: any) {
      console.error("Error getting OAuth token from backend:", error);
      throw error;
    }
  }

  public async refreshOAuthToken(
    refreshToken: string,
    scope: string
  ): Promise<{
    accessToken: string;
    expiresIn: number | null;
    refreshToken: string | null;
  }> {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_BASE_URL_API}/group-calendars/graph-token/refresh`,
        {
          refreshToken: refreshToken,
          scope: scope
        },
        {
          headers: {
            "Content-Type": "application/json"
          }
        }
      );
      return response.data;
    } catch (error: any) {
      console.error("Error refreshing OAuth token from backend:", error);
      throw error;
    }
  }
}

export const graphService = new GraphService();
